/**
 * Digital Twin Assessment Enums
 *
 * Enums for the loan assessment and digital twin platform.
 * Service 18 (Digital Twin)
 */

import { z } from 'zod';

// ============================================================================
// RISK BAND
// ============================================================================

/**
 * Risk classification bands for loan assessments
 */
export enum RiskBand {
  LOW = 'LOW',
  MODERATE = 'MODERATE',
  ELEVATED = 'ELEVATED',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
  PENDING = 'PENDING',
}

/** Zod schema for RiskBand validation */
export const RiskBandSchema = z.nativeEnum(RiskBand);

/** Array of all risk band values for iteration */
export const RISK_BAND_VALUES = Object.values(RiskBand);

// ============================================================================
// TWIN ROLE
// ============================================================================

/**
 * Roles that a digital twin can assume during assessment
 */
export enum TwinRole {
  UNDERWRITER = 'UNDERWRITER',
  COMPLIANCE_OFFICER = 'COMPLIANCE_OFFICER',
  DOC_ANALYST = 'DOC_ANALYST',
  PORTFOLIO_MONITOR = 'PORTFOLIO_MONITOR',
  /**
   * Property-evaluator twin (issue #1617). Production digital-twin-service
   * emits factors with `twinRole: 'PROPERTY_EVALUATOR'` and the DB column
   * (`assessment_factors.twin_role`) accepts free-form strings, so the
   * TwinRole enum must include this value to keep DTO types aligned with
   * what the service actually writes.
   */
  PROPERTY_EVALUATOR = 'PROPERTY_EVALUATOR',
  CUSTOM = 'CUSTOM',
}

/** Zod schema for TwinRole validation */
export const TwinRoleSchema = z.nativeEnum(TwinRole);

/** Array of all twin role values for iteration */
export const TWIN_ROLE_VALUES = Object.values(TwinRole);

// ============================================================================
// ASSESSMENT CATEGORY
// ============================================================================

/**
 * Categories of assessment factors
 */
export enum AssessmentCategory {
  CREDIT = 'CREDIT',
  PROPERTY = 'PROPERTY',
  INCOME = 'INCOME',
  EXPERIENCE = 'EXPERIENCE',
  COMPLIANCE = 'COMPLIANCE',
  DOCUMENT = 'DOCUMENT',
  COLLATERAL = 'COLLATERAL',
  FRAUD = 'FRAUD',
}

/** Zod schema for AssessmentCategory validation */
export const AssessmentCategorySchema = z.nativeEnum(AssessmentCategory);

/** Array of all assessment category values for iteration */
export const ASSESSMENT_CATEGORY_VALUES = Object.values(AssessmentCategory);

// ============================================================================
// ASSESSMENT STATUS
// ============================================================================

/**
 * Status of an overall loan assessment
 */
export enum AssessmentStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETE = 'COMPLETE',
  STALE = 'STALE',
  ERROR = 'ERROR',
}

/** Zod schema for AssessmentStatus validation */
export const AssessmentStatusSchema = z.nativeEnum(AssessmentStatus);

/** Array of all assessment status values for iteration */
export const ASSESSMENT_STATUS_VALUES = Object.values(AssessmentStatus);

// ============================================================================
// FACTOR STATUS
// ============================================================================

/**
 * Status of an individual assessment factor
 */
export enum FactorStatus {
  PASS = 'PASS',
  WARN = 'WARN',
  FAIL = 'FAIL',
  PENDING = 'PENDING',
  NOT_AVAILABLE = 'NOT_AVAILABLE',
}

/** Zod schema for FactorStatus validation */
export const FactorStatusSchema = z.nativeEnum(FactorStatus);

/** Array of all factor status values for iteration */
export const FACTOR_STATUS_VALUES = Object.values(FactorStatus);

// ============================================================================
// ASSESSMENT SOURCE TYPE
// ============================================================================

/**
 * Source of data for an assessment factor
 */
export enum AssessmentSourceType {
  BUREAU = 'BUREAU',
  DOCUMENT = 'DOCUMENT',
  CALCULATION = 'CALCULATION',
  USER_INPUT = 'USER_INPUT',
  AI_INFERENCE = 'AI_INFERENCE',
}

/** Zod schema for AssessmentSourceType validation */
export const AssessmentSourceTypeSchema = z.nativeEnum(AssessmentSourceType);

/** Array of all assessment source type values for iteration */
export const ASSESSMENT_SOURCE_TYPE_VALUES = Object.values(AssessmentSourceType);

/**
 * Descriptive source types emitted by twin scorers.
 * Maps scorer-specific source identifiers to the display-friendly AssessmentSourceType.
 */
export const ScorerSourceType = {
  CREDIT_REPORT: 'credit_report',
  BORROWER_PROFILE: 'borrower_profile',
  PROPERTY_APPRAISAL: 'property_appraisal',
  INCOME_VERIFICATION: 'income_verification',
  DOCUMENT_ANALYSIS: 'document_analysis',
  COMPLIANCE_CHECK: 'compliance_check',
  ECOA_CHECK: 'ecoa_check',
  FAIR_LENDING_ANALYSIS: 'fair_lending_analysis',
  EARLY_WARNING_ANALYSIS: 'early_warning_analysis',
  MODIFICATION_ANALYSIS: 'modification_analysis',
  PAYMENT_ANALYSIS: 'payment_analysis',
} as const;
export type ScorerSourceType = (typeof ScorerSourceType)[keyof typeof ScorerSourceType];

/** Maps scorer source types to their display AssessmentSourceType category */
export const SCORER_SOURCE_DISPLAY_MAP: Record<ScorerSourceType, AssessmentSourceType> = {
  [ScorerSourceType.CREDIT_REPORT]: AssessmentSourceType.BUREAU,
  [ScorerSourceType.BORROWER_PROFILE]: AssessmentSourceType.USER_INPUT,
  [ScorerSourceType.PROPERTY_APPRAISAL]: AssessmentSourceType.DOCUMENT,
  [ScorerSourceType.INCOME_VERIFICATION]: AssessmentSourceType.DOCUMENT,
  [ScorerSourceType.DOCUMENT_ANALYSIS]: AssessmentSourceType.DOCUMENT,
  [ScorerSourceType.COMPLIANCE_CHECK]: AssessmentSourceType.CALCULATION,
  [ScorerSourceType.ECOA_CHECK]: AssessmentSourceType.CALCULATION,
  [ScorerSourceType.FAIR_LENDING_ANALYSIS]: AssessmentSourceType.CALCULATION,
  [ScorerSourceType.EARLY_WARNING_ANALYSIS]: AssessmentSourceType.CALCULATION,
  [ScorerSourceType.MODIFICATION_ANALYSIS]: AssessmentSourceType.CALCULATION,
  [ScorerSourceType.PAYMENT_ANALYSIS]: AssessmentSourceType.CALCULATION,
};

// ============================================================================
// CREDIT BUREAU
// ============================================================================

/**
 * Credit bureau providers
 */
export enum CreditBureau {
  EXPERIAN = 'EXPERIAN',
  EQUIFAX = 'EQUIFAX',
  TRANSUNION = 'TRANSUNION',
  TRI_MERGE = 'TRI_MERGE',
}

/** Zod schema for CreditBureau validation */
export const CreditBureauSchema = z.nativeEnum(CreditBureau);

/** Array of all credit bureau values for iteration */
export const CREDIT_BUREAU_VALUES = Object.values(CreditBureau);

// ============================================================================
// VALUATION SOURCE
// ============================================================================

/**
 * Source of property valuation data
 */
export enum ValuationSource {
  INTAKE_FORM = 'INTAKE_FORM',
  APPRAISAL = 'APPRAISAL',
  AVM = 'AVM',
  MANUAL = 'MANUAL',
}

/** Zod schema for ValuationSource validation */
export const ValuationSourceSchema = z.nativeEnum(ValuationSource);

/** Array of all valuation source values for iteration */
export const VALUATION_SOURCE_VALUES = Object.values(ValuationSource);

// ============================================================================
// ASSESSMENT EVENT TYPE
// ============================================================================

/**
 * Types of events that occur during assessment lifecycle
 */
export enum AssessmentEventType {
  SCORE_UPDATED = 'SCORE_UPDATED',
  FACTOR_ADDED = 'FACTOR_ADDED',
  RECOMMENDATION_CHANGED = 'RECOMMENDATION_CHANGED',
  THRESHOLD_BREACHED = 'THRESHOLD_BREACHED',
  DATA_RECEIVED = 'DATA_RECEIVED',
  HUMAN_OVERRIDE = 'HUMAN_OVERRIDE',
  ERROR = 'ERROR',
}

/** Zod schema for AssessmentEventType validation */
export const AssessmentEventTypeSchema = z.nativeEnum(AssessmentEventType);

/** Array of all assessment event type values for iteration */
export const ASSESSMENT_EVENT_TYPE_VALUES = Object.values(AssessmentEventType);

// ============================================================================
// TWIN STATUS
// ============================================================================

/**
 * Operational status of a digital twin
 */
export enum TwinStatus {
  ACTIVE = 'active',
  PAUSED = 'paused',
  DISABLED = 'disabled',
}

/** Zod schema for TwinStatus validation */
export const TwinStatusSchema = z.nativeEnum(TwinStatus);

/** Array of all twin status values for iteration */
export const TWIN_STATUS_VALUES = Object.values(TwinStatus);

// ============================================================================
// PROPERTY EVALUATION (Issue #1617)
// ============================================================================

/**
 * Underwriter-facing verdict for a property deal evaluation.
 * Phase 1 (triage) + Phase 2 (full memo) emit this.
 */
export enum PropertyVerdict {
  STRONG_FIT = 'STRONG_FIT',
  CONDITIONAL = 'CONDITIONAL',
  POOR_FIT = 'POOR_FIT',
  INSUFFICIENT_DATA = 'INSUFFICIENT_DATA',
}

/** Zod schema for PropertyVerdict validation */
export const PropertyVerdictSchema = z.nativeEnum(PropertyVerdict);

/** Array of all property verdict values for iteration */
export const PROPERTY_VERDICT_VALUES = Object.values(PropertyVerdict);

/**
 * Depth of a property evaluation run. TRIAGE is Phase 1 (fast SFR pull),
 * FULL_MEMO is Phase 2 (Gemini-driven memo), UAD_GRADE is Phase 3 (vision-grade
 * appraiser-style report — not yet active in Phase 1 + Phase 2 scope).
 */
export enum PropertyRunDepth {
  TRIAGE = 'TRIAGE',
  FULL_MEMO = 'FULL_MEMO',
  UAD_GRADE = 'UAD_GRADE',
}

/** Zod schema for PropertyRunDepth validation */
export const PropertyRunDepthSchema = z.nativeEnum(PropertyRunDepth);

/** Array of all property run depth values for iteration */
export const PROPERTY_RUN_DEPTH_VALUES = Object.values(PropertyRunDepth);

/**
 * Source of a property valuation. Note: this is property-evaluation-specific
 * and intentionally distinct from the broader {@link ValuationSource} enum
 * used by other digital-twin paths.
 */
export enum PropertyValuationSource {
  INTAKE_FORM = 'INTAKE_FORM',
  SFR_ANALYTICS = 'SFR_ANALYTICS',
  HOUSECANARY = 'HOUSECANARY',
  RENTCAST = 'RENTCAST',
  MANUAL_OVERRIDE = 'MANUAL_OVERRIDE',
  UAD_VISION = 'UAD_VISION',
}

/** Zod schema for PropertyValuationSource validation */
export const PropertyValuationSourceSchema = z.nativeEnum(PropertyValuationSource);

/** Array of all property valuation source values for iteration */
export const PROPERTY_VALUATION_SOURCE_VALUES = Object.values(PropertyValuationSource);

/**
 * Factor names emitted by property-evaluation scorers. All belong to the
 * {@link AssessmentCategory.PROPERTY} category.
 *
 * Phase 1 (#1617): ARV_CONFIDENCE, COMP_QUALITY, MARKET_TREND, PRICE_VS_AVM.
 * Phase 2 (#1623): REHAB_RISK_BUDGET, CONDITION_RISK, OCCUPANCY_RISK,
 * ZONING_FIT, FLOOD_RISK, DOM_RISK.
 */
export enum PropertyFactorName {
  // Phase 1 (#1617)
  ARV_CONFIDENCE = 'ARV_CONFIDENCE',
  COMP_QUALITY = 'COMP_QUALITY',
  MARKET_TREND = 'MARKET_TREND',
  PRICE_VS_AVM = 'PRICE_VS_AVM',
  // Phase 2 (#1623)
  REHAB_RISK_BUDGET = 'REHAB_RISK_BUDGET',
  CONDITION_RISK = 'CONDITION_RISK',
  OCCUPANCY_RISK = 'OCCUPANCY_RISK',
  ZONING_FIT = 'ZONING_FIT',
  FLOOD_RISK = 'FLOOD_RISK',
  DOM_RISK = 'DOM_RISK',
}

/** Zod schema for PropertyFactorName validation */
export const PropertyFactorNameSchema = z.nativeEnum(PropertyFactorName);

/** Array of all property factor name values for iteration */
export const PROPERTY_FACTOR_NAME_VALUES = Object.values(PropertyFactorName);

/**
 * Rehab line-item category buckets used by the Phase 2 full-memo rehab scope
 * (Issue #1623). Mirrors the seven categories the Gemini rehab prompt is
 * instructed to bucket into.
 */
export enum RehabCategory {
  GENERAL = 'GENERAL',
  PAINT_DRYWALL = 'PAINT_DRYWALL',
  FLOORING = 'FLOORING',
  KITCHEN = 'KITCHEN',
  BATHROOM = 'BATHROOM',
  HVAC = 'HVAC',
  EXTERIOR = 'EXTERIOR',
}

/** Zod schema for RehabCategory validation */
export const RehabCategorySchema = z.nativeEnum(RehabCategory);

/** Array of all rehab category values for iteration */
export const REHAB_CATEGORY_VALUES = Object.values(RehabCategory);

/**
 * Machine-readable severity for `PropertyEvaluationRiskFlag` (Issue #1623 PR
 * review). The Gemini risk-flags prompt emits this alongside the free-form
 * `impact` display text — UIs filter/sort on `severity`, render `impact`
 * verbatim. Uppercase matches the JSON schema `enum: [HIGH, MEDIUM, LOW]`.
 */
export enum PropertyRiskFlagSeverity {
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}

/** Zod schema for PropertyRiskFlagSeverity validation */
export const PropertyRiskFlagSeveritySchema = z.nativeEnum(PropertyRiskFlagSeverity);

/** Array of all property-risk-flag severity values for iteration */
export const PROPERTY_RISK_FLAG_SEVERITY_VALUES = Object.values(PropertyRiskFlagSeverity);
